// ===== FILE: src/pages/DashboardProject/ContractorCategoryPage.tsx ===== (UPDATED)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Button } from 'reactstrap';
import { useParams, useLoaderData, Link } from 'react-router-dom';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import SEOTags from './SEOTags';
import ContractorListingGrid from './ContractorListingGrid';
import ListingPagination from './ListingPagination';
import RelatedCategories from './RelatedCategories';
import PopularLocations from './PopularLocations';
import { useContractorLocations } from '../../hooks/useContractorLocations';
import { Contractor } from '../../models/Contractor.interface';
import { contractorAPI } from '../../services/contractorService';
import Header from "../Home/Header";
import Footer from "../Home/Footer";

interface CategoryPageParams {
  category: string;
}

interface UserLocation {
  city: string;
  state: string;
  stateCode: string;
  latitude?: number;
  longitude?: number;
}

interface CityLocation {
  name: string;
  state: string;
  stateCode: string;
  count: number;
}

// State name to 2-digit code mapping
const STATE_CODE_MAP: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
};

// Reverse mapping for display
const STATE_NAME_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_CODE_MAP).map(([name, code]) => [code, name])
);

const ContractorCategoryPage: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [categoryDisplay, setCategoryDisplay] = useState('');

  // Location detection state
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [topCityLocations, setTopCityLocations] = useState<CityLocation[]>([]);

  const { categorySlug } = useParams<{ categorySlug: string }>();
  const loaderData = useLoaderData() as CategoryPageParams;
  const itemsPerPage = 12;

  // Get location data for the sidebar
  const { locations } = useContractorLocations();

  // Convert state name to 2-digit code
  const getStateCode = (stateName: string): string => {
    const cleanName = stateName.toLowerCase().trim();
    return STATE_CODE_MAP[cleanName] || stateName.toUpperCase().substring(0, 2);
  };

  // Convert 2-digit code to state name
  const getStateName = (stateCode: string): string => {
    return STATE_NAME_MAP[stateCode.toUpperCase()] || stateCode;
  };

  // Location detection logic
  useEffect(() => {
    const detectUserLocation = async () => {
      setLocationLoading(true);

      try {
        // Try browser geolocation first
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;

              try {
                const location = await reverseGeocode(latitude, longitude);
                const stateCode = getStateCode(location.state);

                setUserLocation({
                  city: location.city,
                  state: location.state,
                  stateCode,
                  latitude,
                  longitude
                });
              } catch (error) {
                console.error('Reverse geocoding failed:', error);
                await detectLocationByIP();
              }
            },
            async (error) => {
              console.warn('Geolocation permission denied or failed:', error);
              await detectLocationByIP();
            },
            {
              timeout: 10000,
              enableHighAccuracy: false
            }
          );
        } else {
          console.warn('Geolocation not supported');
          await detectLocationByIP();
        }
      } catch (error) {
        console.error('Location detection failed:', error);
      } finally {
        setLocationLoading(false);
      }
    };

    detectUserLocation();
  }, []);

  // IP-based location detection fallback
  const detectLocationByIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      const stateCode = data.region || 'VA';
      const stateName = getStateName(stateCode);

      setUserLocation({
        city: data.city || 'Virginia Beach',
        state: stateName,
        stateCode,
        latitude: data.latitude,
        longitude: data.longitude
      });
    } catch (error) {
      console.error('IP-based location detection failed:', error);
    }
  };

  // Reverse geocoding function
  const reverseGeocode = async (lat: number, lng: number): Promise<{ city: string; state: string }> => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
      );
      const data = await response.json();

      return {
        city: data.city || data.locality || 'Virginia Beach',
        state: data.principalSubdivision || 'Virginia'
      };
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      throw error;
    }
  };

  // Process locations to get top city locations for sidebar
  useEffect(() => {
    if (locations.length > 0) {
      const cityLocations = locations
        .filter(location => location.city && location.state)
        .map(location => ({
          name: location.city!,
          state: location.state,
          stateCode: getStateCode(location.state),
          count: location.count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      setTopCityLocations(cityLocations);
    }
  }, [locations]);

  // Convert category slug back to display name
  useEffect(() => {
    if (loaderData.category) {
      const display = loaderData.category
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setCategoryDisplay(display);
    }
  }, [loaderData.category]);

  // Fetch contractors in this category
  useEffect(() => {
    const fetchContractors = async () => {
      if (!loaderData.category) return;

      setLoading(true);
      setError(null);

      try {
        const response = await contractorAPI.getContractorsByCategory(loaderData.category, {
          page: currentPage,
          limit: itemsPerPage
        });

        setContractors(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalCount(response.pagination.totalCount);
      } catch (err) {
        console.error('Error fetching contractors:', err);
        setError('Failed to load contractors. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchContractors();
  }, [loaderData.category, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLocationSelect = (stateCode: string, city?: string) => {
    // Use the stateCode directly (already in 2-letter format like "VA")
    const stateSlug = stateCode.toLowerCase(); // "va" 
    const citySlug = city?.toLowerCase().replace(/ /g, '-');

    if (city) {
      window.location.href = `/contractors/${stateSlug}/${citySlug}/${loaderData.category}`;
    } else {
      window.location.href = `/contractors/${stateSlug}/${loaderData.category}`;
    }
  };

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        <SEOTags
          title={`${categoryDisplay} Contractors`}
          description={`Find professional ${categoryDisplay} contractors. Browse our directory of qualified ${categoryDisplay} contractors for your project needs.`}
          schemaType="ItemList"
        />

        <Container fluid>
          <AutoBreadCrumb />

          <Row>
            <Col lg={9}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {categoryDisplay} Contractors ({totalCount} found)
                  </h4>
                </CardHeader>

                <CardBody>
                  {error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : (
                    <>
                      <ContractorListingGrid
                        contractors={contractors}
                        loading={loading}
                      />

                      {contractors.length === 0 && !loading && (
                        <div className="text-center py-4">
                          <p className="text-muted">No {categoryDisplay} contractors found.</p>
                        </div>
                      )}

                      {totalPages > 1 && (
                        <ListingPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              {/* Popular Locations for this Category */}
              <Card className="mb-4 sticky-top" style={{ top: '20px' }}>
                <CardBody>
                  <h5>
                    <i className="fas fa-fire text-danger me-2"></i>
                    Popular Locations
                  </h5>

                  {topCityLocations.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {topCityLocations.map((location, index) => (
                        <button
                          key={index}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          onClick={() => handleLocationSelect(location.stateCode, location.name)}
                        >
                          <div>
                            <div className="fw-medium">{location.name}</div>
                            <small className="text-muted">{location.state}</small>
                          </div>
                          <Badge color="primary" pill>
                            {location.count}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2 mb-0">Loading locations...</p>
                    </div>
                  )}

                  {/* User Location Display */}
                  {userLocation && (
                    <div className="mt-3 pt-3 border-top">
                      <h6>Near You</h6>
                      <Button
                        color="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleLocationSelect(userLocation.stateCode, userLocation.city)}
                      >
                        <i className="fas fa-map-marker-alt me-2"></i>
                        {categoryDisplay} in {userLocation.city}
                      </Button>
                    </div>
                  )}

                  {/* Browse All Link */}
                  <div className="text-center mt-3 pt-3 border-top">
                    <Link to="/contractors/locations" className="btn btn-link btn-sm">
                      View All Locations
                    </Link>
                  </div>
                </CardBody>
              </Card>

              <RelatedCategories
                currentCategory={loaderData.category}
                contractors={contractors}
              />
              <PopularLocations
                category={loaderData.category}
                contractors={contractors}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ContractorCategoryPage;